import { useCallback, useMemo, useState } from 'react';
import { CustomSelect } from '@/components';
import { withCurrentOption } from '../utils/companyPreferenceOptions';
import { getCurrencyFallbackLabel } from '../utils/companyCurrencies';
import { useCompanyCurrencies } from '../hooks/useCompanyCurrencies';

function getCurrenciesErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    'Failed to load currencies.'
  );
}

export function CurrencySelect({
  value,
  onChange,
  disabled = false,
  error = false,
  className = 'w-full h-[40px] sm:h-[44px]',
  placeholder = 'Select currency',
  searchPlaceholder = 'Search currency...',
  readOnly = false,
}) {
  const [search, setSearch] = useState('');
  const {
    options: fetchedOptions,
    isPending,
    isError,
    error: fetchError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCompanyCurrencies(search);

  const options = useMemo(
    () => withCurrentOption(fetchedOptions, value, getCurrencyFallbackLabel),
    [fetchedOptions, value],
  );

  const handleSearchChange = useCallback((query) => {
    setSearch(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const displayLabel =
    options.find((opt) => String(opt.value) === String(value))?.label ||
    getCurrencyFallbackLabel(value) ||
    '—';

  if (readOnly) {
    return <p className="text-[15px] font-medium text-black">{displayLabel}</p>;
  }

  const errorMessage = isError ? getCurrenciesErrorMessage(fetchError) : '';

  return (
    <>
      <CustomSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        isLoading={isPending}
        searchable
        searchPlaceholder={searchPlaceholder}
        error={error || isError}
        emptyMessage={isError ? errorMessage : 'No currencies available'}
        onSearchChange={handleSearchChange}
        onLoadMore={handleLoadMore}
        hasMore={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        className={className}
      />
      {isError ? (
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="text-status-error text-sm font-medium">
            {errorMessage}
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium text-primary shrink-0"
          >
            Retry
          </button>
        </div>
      ) : null}
    </>
  );
}
